// =============================================================================
// app/settings/muted.tsx — muted users list + unmute (bare-RN port).
//
// MIGRATION (P3 settings): @expo/vector-icons -> components/ui/Icon; the inline
// expo-router <Stack.Screen options> headers are dropped (SettingsStack owns the
// title). All API/business logic is unchanged.
//
// APPLE-STANDARD PASS (matches blocked.tsx): flat colors.bg replaced with a
// full-bleed <ParticleBackground/> aurora (absoluteFill, under the SettingsStack
// header). The scattered per-row cards are consolidated into GlassCard rows, a
// section caption + count pill give context, the empty/error states get a
// floated icon chip, and typography is tightened per skill §15 (weight+size
// hierarchy, negative tracking on titles, body near 0). Unmute stays a scoped
// pill that responds on press (§1) — primary-toned since unmuting is a benign,
// reversible action, not destructive. Data/nav/a11y untouched.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Image,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard, GlassButton, glassTokens } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';

interface MutedUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url?: string;
}

export default function MutedScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<MutedUser[]>([]);

  const fetchMuted = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Backend returns data: [{ id: muteRecordId, createdAt, user: {...} }].
    // Map to MutedUser using the USER's id so unmute targets the right user via
    // DELETE /users/:userId/mute.
    const res = await api<Array<{ user: { id: string; username: string; displayName?: string; avatar?: string } }>>('/settings/muted');
    if (res.success && res.data) {
      const list = Array.isArray(res.data) ? res.data : [];
      setUsers(
        list
          .filter((m) => m && m.user)
          .map((m) => ({
            id: m.user.id,
            username: m.user.username,
            display_name: m.user.displayName || '',
            avatar_url: m.user.avatar,
          }))
      );
    } else if (!res.success) {
      setError(res.error || 'Failed to load muted users.');
      setUsers([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMuted();
  }, [fetchMuted]);

  function handleUnmute(user: MutedUser) {
    Alert.alert(
      'Unmute User',
      `Are you sure you want to unmute @${user.username}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unmute',
          onPress: async () => {
            const res = await api(`/users/${user.id}/mute`, {
              method: 'DELETE',
            });
            if (res.success) {
              setUsers((prev) => prev.filter((u) => u.id !== user.id));
              Alert.alert('Done', `@${user.username} has been unmuted.`);
            } else {
              Alert.alert('Error', res.error || 'Failed to unmute user.');
            }
          },
        },
      ]
    );
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.emptyWrap}>
          <View style={styles.emptyIcon}>
            <Ionicons name="alert-circle-outline" size={30} color={colors.error} />
          </View>
          <Text style={styles.emptyTitle}>Couldn't load</Text>
          <Text style={styles.emptyDesc}>{error}</Text>
          <GlassButton
            title="Retry"
            onPress={fetchMuted}
            variant="secondary"
            size="sm"
            style={styles.retryBtn}
          />
        </View>
      </View>
    );
  }

  const count = Array.isArray(users) ? users.length : 0;

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <FlatList
        data={Array.isArray(users) ? users : []}
        keyExtractor={(item) => item.id}
        contentContainerStyle={[styles.listContent, containerStyle]}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          count > 0 ? (
            <View style={styles.listHeader}>
              <Text style={styles.sectionTitle}>Muted</Text>
              <View style={styles.countPill}>
                <Text style={styles.countText}>{count}</Text>
              </View>
            </View>
          ) : null
        }
        ListEmptyComponent={
          <View style={styles.emptyWrap}>
            <View style={styles.emptyIcon}>
              <Ionicons name="volume-mute-outline" size={30} color={colors.primary} />
            </View>
            <Text style={styles.emptyTitle}>No muted users</Text>
            <Text style={styles.emptyDesc}>
              Anyone you mute will appear here. You won't see their posts, but they can still follow and message you.
            </Text>
          </View>
        }
        renderItem={({ item }) => (
          <GlassCard padding={0} style={styles.card}>
            <View style={styles.userRow}>
              <View style={styles.userLeft}>
                {item.avatar_url ? (
                  <Image
                    source={{ uri: item.avatar_url }}
                    style={styles.avatar}
                  />
                ) : (
                  <View style={[styles.avatar, styles.avatarFallback]}>
                    <Text style={styles.avatarLetter}>
                      {(item.display_name || item.username || 'U')[0].toUpperCase()}
                    </Text>
                  </View>
                )}
                <View style={{ flex: 1 }}>
                  <Text style={styles.displayName} numberOfLines={1}>
                    {item.display_name || item.username || 'Unknown user'}
                  </Text>
                  <Text style={styles.username} numberOfLines={1}>
                    @{item.username || 'unknown'}
                  </Text>
                </View>
              </View>
              <TouchableOpacity
                style={styles.unmuteBtn}
                onPress={() => handleUnmute(item)}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel={`Unmute ${item.username || 'user'}`}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Text style={styles.unmuteText}>Unmute</Text>
              </TouchableOpacity>
            </View>
          </GlassCard>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: spacing.lg,
    flexGrow: 1,
  },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
    marginLeft: spacing.xs,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  countPill: {
    minWidth: 22,
    height: 20,
    paddingHorizontal: 7,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '18',
  },
  countText: {
    fontSize: 11,
    color: colors.primary,
    ...fonts.bold,
  },
  emptyWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 80,
    paddingHorizontal: spacing.xl,
    gap: spacing.sm,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: glassTokens.border,
    marginBottom: spacing.xs,
    ...shadowSoft(),
  },
  emptyTitle: {
    ...typographyScale.h3,
    fontSize: 19,
    letterSpacing: -0.3,
    color: colors.ink,
    ...fonts.bold,
  },
  emptyDesc: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    ...fonts.regular,
  },
  retryBtn: {
    marginTop: spacing.md,
  },
  card: {
    marginBottom: spacing.sm,
  },
  userRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  userLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  avatarFallback: {
    backgroundColor: colors.primary + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarLetter: {
    fontSize: 18,
    color: colors.primary,
    ...fonts.bold,
  },
  displayName: {
    fontSize: 15,
    letterSpacing: -0.1,
    color: colors.ink,
    ...fonts.semibold,
  },
  username: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  unmuteBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.primary + '33',
    backgroundColor: colors.primary + '0F',
  },
  unmuteText: {
    fontSize: 13,
    color: colors.primary,
    ...fonts.semibold,
  },
});

// Soft lift for the empty-state icon chip — keeps it from looking flat over the
// aurora without pulling in the heavier card elevation.
function shadowSoft() {
  return {
    shadowColor: 'rgba(108,92,231,0.16)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 3,
  } as const;
}
